<template>
  <section class="w-full flex justify-center">
    <Attention v-if="hasAttention" />
    <div class="w-full h-[40vh] md:h-[50vh] lg:h-[76vh]">
      <video ref="player" class="video-js vjs-theme-fantasy"></video>
    </div>

    <Dialog
      v-model:visible="confirmModal"
      :closable="false"
      :draggable="false"
      :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
      :style="{ width: '50vw' }"
      :modal="true"
      :showHeader="false"
      contentStyle="background-color: #E0EFFF;border-bottom:0.4rem solid #d2b604;border-radius:0.5rem;padding:0"
      @hide="closeModalAttention()"
    >
      <section class="p-5">
        <div class="grid grid-cols-12 items-center gap-5">
          <div class="col-span-12 md:col-span-3 flex justify-center">
            <img
              :src="require('@/assets/images/avatares/repproved.gif')"
              class="w-4/6 md:w-5/6 rounded-full"
              alt="repproved img"
              loading="lazy"
            />
          </div>
          <div class="col-span-12 md:col-span-9 flex flex-col gap-5">
            <div class="flex flex-wrap justify-between gap-3 items-center">
              <h4 class="">
                {{ $t("attention.title", { user: user?.firstName }) }}
              </h4>
              <MessageVoice :msg="attentionText()" class="w-fit" />
            </div>
            <p>{{ $t("attention.subtitle") }}</p>
            <p
              class="flex gap-2 items-center justify-center mt-5 text-xs text-gray-600 transition-all animate-errorBlink"
            >
              <v-icon name="hi-information-circle" :scale="1.2" />
              <span>{{ $t("attention.info") }}</span>
            </p>
          </div>
        </div>
        <div class="flex mt-5 justify-end">
          <Button
            :label="$t('attention.accept')"
            class="btn-primary p-button-sm h-10"
            @click="closeModalAttention()"
          />
        </div>
      </section>
    </Dialog>
  </section>
</template>

<script lang="ts">
import { AttentionRest } from "@/services/rest/attention.rest";
import { useVideoStore } from "@/store/video";
import { LogActions } from "@/utils/log-actions.util";
import { getUserFromStorage } from "@/utils/user-store.util";
import { CourseStudentService } from "@/views/CourseStudent/course-student.service";
import Hls from "hls.js";
import { debounceTime, Subscription } from "rxjs";
import videojs from "video.js";
import videojsContribQualityLevels from "videojs-contrib-quality-levels";
import videojsHlsQualitySelector from "videojs-hls-quality-selector";
import { defineComponent } from "vue";
import { AttentionService } from "../Attention/attention.service";
import { currentVideoTime$, videoEnded$ } from "./options";

export default defineComponent({
  name: "VideoComponent",
  emits: ["onCompleteVideo", "onPause"],
  computed: {
    service(): CourseStudentService {
      return new CourseStudentService();
    },
    user(): any {
      return getUserFromStorage();
    },
    serviceAttention(): AttentionService {
      return new AttentionService();
    },
    attentionRest(): AttentionRest {
      return new AttentionRest();
    },
    classroomID(): string | undefined {
      return localStorage.getItem("classId") ?? undefined;
    },
    classroomName(): string | undefined {
      return localStorage.getItem("className") ?? undefined;
    },
  },
  props: {
    videoTitle: {
      type: String,
      default: "Nome do vídeo",
    },
    idVideo: {
      type: String,
      default: "0",
    },
    data: {
      required: true,
      type: Object,
    },
    startTime: {
      type: Number,
      default: 0,
    },
    attention: {
      type: Boolean,
      default: true,
    },
    isAdaptativeLearning: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      player: {} as any,
      hls: new Hls(),
      hasPause: false as boolean,
      currentVideosTime: {} as Object,
      hasAttention: false as boolean,
      subscription: new Subscription(),
      confirmModal: false as boolean,
      loading: false as boolean,
    };
  },
  watch: {
    startTime() {
      this.player.currentTime(this.startTime);
    },
  },
  created() {
    this.hasAttention = false;
  },
  mounted() {
    this.setVideo();
    if (sessionStorage.getItem("currentVideosTime")) {
      let current: any = sessionStorage.getItem("currentVideosTime");
      this.currentVideosTime = JSON.parse(current);
    }

    setTimeout(() => useVideoStore.setPlayer(this.player), 250);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  },

  unmounted() {
    if (this.player) this.player.dispose();
    this.hasAttention = false;
    this.subscription.unsubscribe();
    useVideoStore.$reset();

    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
  },

  methods: {
    handleVisibilityChange(): void {
      if (document.hidden) this.player.pause();
      else this.player.play();
    },
    setVideo(): void {
      this.loading = true;
      if (Hls.isSupported() && this.data.url) {
        const options = {
          controls: true,
          responsive: true,
          sources: [
            {
              src: this.data.url,
              type: "application/x-mpegURL",
            },
          ],
          plugins: { hlsQualitySelector: { default: "auto" } },
        };

        this.player = videojs(
          this.$refs.player as HTMLMediaElement,
          options,
          () => {
            this.loading = false;
            this.player.fill(true);
            this.player.playbackRates([0.5, 1, 1.5, 2]);
            this.startInCurrentTime();
            this.checkVideoCanStart();
            this.checkVideoEnded();
            this.videoError();
            this.needsToPause(true);
            if (this.user.role === "STUDENT") {
              this.attentionTriggers();
              this.getAttention();
            }
          }
        );
      }
    },
    startInCurrentTime(): void {
      let currentTime: any = this.currentVideosTime[this.idVideo]
        ? this.currentVideosTime[this.idVideo]
        : String(this.startTime);
      this.player.currentTime(parseFloat(currentTime));
    },
    videoError(): void {
      this.player.on("error", () => {
        if (!this.player.error()) return;
        this.player.errorDisplay.contentEl().innerHTML = this.$t(
          "studentCourse.errors.video"
        );
      });
    },
    checkVideoEnded(): void {
      this.player.on("timeupdate", () => {
        const playing = !this.player.paused();
        const currentTime = this.player.currentTime();
        const duration = this.player.duration();
        this.currentVideosTime[this.idVideo] = currentTime;
        sessionStorage.setItem(
          "currentVideosTime",
          JSON.stringify(this.currentVideosTime)
        );

        currentVideoTime$.next(currentTime);
        if (currentTime / duration > 0.9) {
          videoEnded$.next(true);
          useVideoStore.setVideoEnded(true);
        } else {
          videoEnded$.next(false);
          useVideoStore.setVideoEnded(false);
        }
        if (!playing) this.hasAttention = false;
      });
    },
    checkVideoCanStart(): void {
      this.player.on("loadstart", () => {
        const currentTime = this.player.currentTime();
        const duration = this.player.duration();
        currentVideoTime$.next(currentTime);
        if (currentTime / duration > 0.9) videoEnded$.next(true);
        else videoEnded$.next(false);
      });
    },
    configQualityLevels(): void {
      const controlBar = this.player?.controlBar;
      if (!controlBar) return;

      const qualityButton = controlBar
        .children()
        .find((e) => e?.name() === "QualityButton");
      if (qualityButton) qualityButton.el()?.remove();

      this.player["qualityLevels"] = videojsContribQualityLevels;
      this.player["qualityLevels"]();

      this.player["hlsQualitySelector"] = videojsHlsQualitySelector;
      this.player["hlsQualitySelector"]({
        vjsIconClass: "vjs-icon-hd",
      });
    },
    needsToPause(value: boolean): void {
      if (value === false) this.player.play();
      else this.player.pause();
    },
    attentionTriggers(): void {
      this.player.on("play", () => {
        if (this.attention) this.hasAttention = true;
      });

      this.player.on("pause", () => {
        if (this.attention) this.hasAttention = false;
      });
    },
    getAttention(): void {
      this.subscription = AttentionService.attention
        .pipe(debounceTime(150))
        .subscribe({
          next: (e) => {
            if (e && e.level === 0 && this.hasAttention === true) {
              this.confirmModal = true;
              this.sendLog();
              this.player.pause();
            }
          },
        });
    },
    sendLog(): void {
      let minute = this.convertTime(
        Math.round((this.player.currentTime() % 3600) / 60)
      );
      let second = this.convertTime(
        Math.round(this.player.currentTime() % 3600) % 60
      );
      let data = {
        action: LogActions.LOST_ATTENTION,
        user: this.user.id,
        userName: this.user.userName,
        course: this.$route.params.id,
        courseName: this.$route.params.courseName,
        material: this.data.id,
        materialName: this.data.name,
        date: new Date(),
        time: minute + ":" + second,
        classroomId: this.classroomID ?? undefined,
        classroomName: this.classroomName ?? undefined,
      };
      this.attentionRest.attentionLog(data).subscribe({
        next: () => {},
        error: (e) => {
          console.error(e);
        },
      });
    },
    convertTime(value: number): string {
      let result = String(value);
      if (value <= 9) result = "0" + value;
      return result;
    },
    attentionText(): string {
      return this.$t("attention.title", { user: this.user?.firstName })?.concat(
        this.$t("attention.subtitle")
      );
    },
    closeModalAttention(): void {
      this.confirmModal = false;
      this.player.play();
    },
  },
});
</script>

<style lang="scss" src="./style.scss"></style>
